/**
 * React Query hooks for purchase order operations
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { purchaseOrderApi } from '../services/procurementApi';
import type {
  PurchaseOrderCreate,
  PurchaseOrderUpdate,
  PurchaseOrderEmailRequest,
  InstallmentPrepayRequest,
  ListPurchaseOrdersParams,
  ListVendorOrdersParams,
} from '../types';

export const purchaseOrderKeys = {
  all: ['procurement-purchase-orders'] as const,
  list: (params?: ListPurchaseOrdersParams) => [...purchaseOrderKeys.all, 'list', params] as const,
  detail: (id: string) => [...purchaseOrderKeys.all, 'detail', id] as const,
  // Keyed by the vendor *profile* id. Lives under `all` so every PO mutation's
  // `invalidateQueries(purchaseOrderKeys.all)` also refreshes a vendor's PO tab.
  // The page params are part of the key — the history is server-side paginated.
  byVendor: (vendorProfileId: string, params?: ListVendorOrdersParams) =>
    [...purchaseOrderKeys.all, 'by-vendor', vendorProfileId, params] as const,
};

function cleanParams(params?: ListPurchaseOrdersParams): ListPurchaseOrdersParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListPurchaseOrdersParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function usePurchaseOrders(params?: ListPurchaseOrdersParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => cleanParams(params), [
    params?.status,
    params?.vendor_id,
    params?.is_intercompany,
    params?.admin_scope,
  ]);

  return useQuery({
    queryKey: purchaseOrderKeys.list(stableParams),
    queryFn: () => purchaseOrderApi.list(stableParams),
  });
}

export function usePurchaseOrder(id: string) {
  return useQuery({
    queryKey: purchaseOrderKeys.detail(id),
    queryFn: () => purchaseOrderApi.get(id),
    enabled: !!id,
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PurchaseOrderCreate) => purchaseOrderApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    },
  });
}

export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PurchaseOrderUpdate }) => purchaseOrderApi.update(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

export function useApprovePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.approve(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

/**
 * Record a vendor prepayment (deposit) against an unbilled PO installment.
 * Posts DR Vendor Prepayments / CR Bank via accounting and marks the installment
 * paid. Refreshes the PO so the schedule reflects the new status + payment links.
 */
export function usePrepayInstallment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      installmentId,
      data,
    }: {
      orderId: string;
      installmentId: string;
      data: InstallmentPrepayRequest;
    }) => purchaseOrderApi.prepayInstallment(orderId, installmentId, data),
    onSuccess: (_data, { orderId }) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(orderId) });
      // Paying a deposit removes it from accounting's Payments Due worklist —
      // invalidate that query by its key prefix (no cross-module import).
      queryClient.invalidateQueries({ queryKey: ['payments-due'] });
    },
  });
}

export function useSendPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.send(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

export function useCancelPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.cancel(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

export function useClosePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.close(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

export function useCopyPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.copy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    },
  });
}

export function useReleaseBlanketOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.releaseBlanket(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
    },
  });
}

export function useStageVoucher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => purchaseOrderApi.stageVoucher(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.detail(id) });
    },
  });
}

export function useEmailPurchaseOrder() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PurchaseOrderEmailRequest }) =>
      purchaseOrderApi.email(id, data),
  });
}
